import { FC, useEffect, useRef, useState, memo, useMemo } from 'react';

import { Link, useNavigate } from '@remix-run/react';
import { AuctionImages } from '~/components/auction-images';
import { Button } from '~/components/ui/button';
import { formatDateTime } from '~/lib/utils';
import type { getAuctionsList, getHomepageAuctionsList } from '~/services/api/auctions';
import { AuctionType } from '~/types';
import { Calendar, Eye, Tag, Video } from 'lucide-react';

type HomepageAuction = Awaited<ReturnType<typeof getHomepageAuctionsList>>['list'][0];
type FullAuction = Awaited<ReturnType<typeof getAuctionsList>>['list'][0];
type AuctionItemData = HomepageAuction | FullAuction;

interface AuctionsProps {
  data: Awaited<ReturnType<typeof getHomepageAuctionsList>>;
  userId?: string | null;
}

export const AuctionItem: FC<{
  auction: AuctionItemData;
  userId?: string | null;
}> = memo(({ auction, userId }) => {
  const [isAuctionStarted, setIsAuctionStarted] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const navigate = useNavigate();

  // Memoize computed values to prevent recalculation
  const auctionInfo = useMemo(() => {
    const now = new Date();
    const auctionStart = new Date(auction.auctionStart);
    const auctionEnd = new Date(auction.auctionEnd);
    const isLive =
      'isLive' in auction ? auction.isLive : auction.products.some((product) => product.liveBiddingMode);
    const auctionEnded = now >= auctionEnd;
    const auctionImages =
      'images' in auction
        ? auction.images
        : auction.products.filter((p) => p.images.length > 0).map((p) => p.images[0]);

    return {
      auctionStart,
      auctionEnd,
      isLive,
      auctionEnded,
      auctionImages
    };
  }, [auction]);

  useEffect(() => {
    if (auction && !auctionInfo.auctionEnded) {
      const updateAuctionStatus = () => {
        const now = new Date();
        const auctionStarted = now >= auctionInfo.auctionStart;
        const auctionEnded = now >= auctionInfo.auctionEnd;
        
        setIsAuctionStarted(auctionStarted && !auctionEnded);

        if (auctionEnded && intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      };

      // Use longer interval for better performance (5 seconds instead of 1 second for non-live auctions)
      const interval = auctionInfo.isLive ? 1000 : 5000;
      intervalRef.current = setInterval(updateAuctionStatus, interval);
      updateAuctionStatus();

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      };
    }
  }, [auction, auctionInfo]);

  const { isLive, auctionEnded, auctionImages } = auctionInfo;

  return (
    <div className="relative flex w-full basis-1/4 flex-col gap-3 bg-slate-100">
      {isLive && (
        <div className="absolute flex flex-row items-center gap-1 bg-red-500 px-1 text-white">
          <p className="text-xs font-normal">Live now </p>
          <Eye className="h-3 w-3" />
        </div>
      )}

      <div className="flex w-full flex-row justify-center space-x-[1px]">
        <AuctionImages images={auctionImages} />
      </div>
      <div className="mb-4 flex flex-col gap-2 px-[5%]">
        <Link to={`/auction-house/${auction.auctionHouse?.slug}`}>
          <p className="text-xs font-normal text-[#646464] hover:underline">{auction.auctionHouse?.name}</p>
        </Link>
        <p className="text-sm font-medium text-black">{auction.name}</p>
        <div className="flex flex-row gap-2">
          <Tag className="h-3 w-3" color="#646464" />
          <p className="text-xs font-normal text-[#646464]">
            {auction.auctionType === AuctionType.TIMEONLINE ? 'TimeOnline' : 'Time2Live'}
          </p>
        </div>
        <div className="flex flex-row gap-2">
          <Calendar className="h-3 w-3" color="#646464" />
          <p className="text-xs font-normal text-[#646464]">Start: {formatDateTime(auction.auctionStart)} GMT+8</p>
        </div>
        {auction.auctionType === AuctionType.TIMEONLINE ? (
          <div className="flex flex-row gap-2">
            <Calendar className="h-3 w-3" color="#646464" />
            <p className="text-xs font-normal text-[#646464]">End: {formatDateTime(auction.auctionEnd)} GMT+8</p>
          </div>
        ) : auction.auctionType === AuctionType.TIME2LIVE && isLive ? (
          <div className="flex flex-row items-center gap-2">
            <Video className="h-3 w-3" color="#646464" />
            <p className="text-xs text-red-500">Auction is Live. Bid now!</p>
          </div>
        ) : (
          <div className="flex flex-row items-center gap-2">
            <Video className="h-3 w-3" color="#646464" />
            {auctionEnded ? (
              <p className="text-xs text-slate-500">Auction ended</p>
            ) : auction.liveAuctionDate ? (
              <p className="text-xs font-normal text-[#646464]">Live auction: {formatDateTime(auction.liveAuctionDate)} GMT+8</p>
            ) : null}
          </div>
        )}
        <Button
          variant={'primary'}
          disabled={!isAuctionStarted}
          onClick={() => {
            navigate(`/auction-detail/${auction.slug}`);
          }}
        >
          {userId && auction.auctionType === AuctionType.TIMEONLINE
            ? 'Bid Now'
            : userId && auction.auctionType === AuctionType.TIME2LIVE
              ? 'Enter Live Auction'
              : 'Register to Bid'}
        </Button>
        <Link to={`/auction-detail/${auction.slug}`}>
          <Button variant={'outline'} className="w-full">
            View Collection
          </Button>
        </Link>
      </div>
    </div>
  );
});

const Auctions: FC<AuctionsProps> = ({ data, userId }) => {
  return (
    <div className="flex flex-col py-5">
      <p className="text-xl font-medium text-black">Explore Auctions</p>
      <p className="mb-4 text-xs font-normal text-[#646464]">{data.totalCount} auctions found</p>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        {data.list.map((auction) => (
          <AuctionItem key={auction.id} auction={auction} userId={userId} />
        ))}
      </div>
      <Link className="my-10 flex justify-center" to="/auctions">
        <Button variant={'outline'}>Explore All Auctions</Button>
      </Link>
    </div>
  );
};

export default Auctions;
